//src/app/features/conversations/ConversationProvider.tsx
import {
  useGetConversationByIdQuery,
  useLazyGetConversationMessagesQuery,
} from 'app/services/conversations';
import { IConversation, IMessage } from 'app/services/messages';
import { IUser } from 'app/services/user';
import { uniqBy } from 'lodash';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';
import { useUnreadConversations } from 'utils/ConversationsNotificationsProvider';
import useConversationId from './Hooks/useConversationId';

export const ConversationProvierContext = createContext<{
  addScrollOffset: boolean | undefined;
  loadNext: () => void;
  loadPrevious: () => void;
  newMessageId: number | undefined;
  scrollToMessageId: number | undefined;
  hasMoreNext: boolean | undefined;
  hasMorePrev: boolean | undefined;
  conversation: IConversation | undefined;
  conversationId: number;
  appendMessage: (message: IMessage) => void;
  messages: IMessage[] | undefined;
  conversationUsers: { [key: number]: IUser };
  isLoadingConversation: boolean;
}>({} as any);

const EmptyMessagesResponse = {
  result: [],
  pagination: { next_position: null },
};
export default function ConversationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { userId } = useAuth();
  const conversationId = useConversationId();
  const { clientId } = useParams();
  const { unreadConversations } = useUnreadConversations();

  const { data: conversation, isFetching: isLoadingConversation } =
    useGetConversationByIdQuery(conversationId, {
      skip: !conversationId,
    });
  const [getMessages] = useLazyGetConversationMessagesQuery();

  const [state, setState] = useState<{
    messages?: IMessage[];
    newMessageId?: number;
    scrollToMessageId?: number;
    addScrollOffset?: boolean;
    hasMoreNext?: boolean;
    hasMorePrev?: boolean;
  }>({});

  const {
    addScrollOffset,
    messages,
    newMessageId,
    scrollToMessageId,
    hasMoreNext,
    hasMorePrev,
  } = state;

  const getPreviousMessages = (start_from: number) =>
    getMessages({
      limit: 5,
      conversationId,
      direction: 'previous',
      start_from,
      client_id: clientId,
    })
      .unwrap()
      .then((response) => ({
        ...response,
        result: response.result.slice().reverse(),
      }));

  const getNextMessages = (start_from: number) =>
    getMessages({
      limit: 5,
      conversationId,
      direction: 'next',
      start_from,
      client_id: clientId,
    }).unwrap();

  const initialLoad = () => {
    getMessages({
      conversationId,
      limit: 1,
      setAsRead: true,
      client_id: clientId,
    })
      .unwrap()
      .then((cursorPage) => {
        if (!conversation) return;
        const cursorId = cursorPage.result[0].id;

        const previousPromise =
          cursorPage.pagination.count > 1
            ? getPreviousMessages(cursorId)
            : Promise.resolve(EmptyMessagesResponse);

        const nextPromise = cursorPage.pagination.next_position
          ? getNextMessages(cursorPage.pagination.next_position)
          : Promise.resolve(EmptyMessagesResponse);

        Promise.all([previousPromise, nextPromise]).then(([prev, next]) => {
          setState({
            messages: uniqBy(
              [...prev.result, ...cursorPage.result, ...next.result],
              'id',
            ),
            newMessageId: !cursorPage.result[0].read_by
              .map((u) => u.id)
              .includes(userId!)
              ? cursorId
              : undefined,
            scrollToMessageId: cursorId,
            hasMoreNext: !!next.pagination.next_position,
            hasMorePrev: !!prev.pagination.next_position,
            addScrollOffset: false,
          });
        });
      });
  };

  const loadPrevious = () => {
    if (!messages) return;
    getPreviousMessages(messages[0].id).then((prev) => {
      setState({
        ...state,
        messages: uniqBy([...prev.result, ...messages], 'id'),
        hasMorePrev: !!prev.pagination.next_position,
        scrollToMessageId: prev.pagination.current_position,
        addScrollOffset: true,
      });
    });
  };

  const loadNext = () => {
    if (!messages) return;
    getNextMessages(messages[messages.length - 1].id).then((next) => {
      setState({
        ...state,
        messages: uniqBy([...messages, ...next.result], 'id'),
        hasMoreNext: !!next.pagination.next_position,
        scrollToMessageId: next.pagination.current_position,
        addScrollOffset: false,
      });
    });
  };

  const conversationUsers: { [id: number]: IUser } = useMemo(
    () =>
      (conversation?.users || []).reduce(
        (acc, cur) => ({ ...acc, [cur.id]: cur }),
        {},
      ),
    [conversation],
  );

  useEffect(() => {
    if (!unreadConversations) return;

    const hasNewMessages = unreadConversations.find(
      (_) => _.conversation_id === conversation?.id,
    );

    if (!!hasNewMessages && !state.hasMoreNext) {
      // get new messages and scrolls to latest message only if all messages are loaded
      loadNext();
    }
  }, [JSON.stringify(unreadConversations)]);
  const appendMessage = (message: IMessage) => {
    state.hasMoreNext
      ? initialLoad()
      : setState({
          ...state,
          messages: uniqBy([...(state.messages || []), message], 'id'),
          hasMoreNext: false,
          scrollToMessageId: message.id,
          addScrollOffset: false,
        });
  };

  useEffect(() => setState({}), [conversationId]); // reset state on route change
  useEffect(initialLoad, [conversation?.id]); // init cursor page on conversation load

  const value = {
    addScrollOffset,
    conversation,
    loadNext,
    loadPrevious,
    newMessageId,
    scrollToMessageId,
    isLoadingConversation,
    hasMoreNext,
    hasMorePrev,
    conversationId,
    messages,
    conversationUsers,
    appendMessage,
  };
  return (
    <ConversationProvierContext.Provider value={value}>
      {children}
    </ConversationProvierContext.Provider>
  );
}
